import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem, Button, Typography,
  Stack, Divider, Alert, CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useParams } from 'react-router-dom';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import useCustomFields from '../../hooks/useCustomFields';
import ModuleCustomFields from '../dynamic/ModuleCustomFields';
import configService from '../../services/configService';
import { departmentsForBranch } from '../../utils/departmentBranches';
import { workTypeOptions } from '../../utils/workTypeOptions';
import { useContractPayRates } from '../../utils/contractPay';
import { contractPayFromBase, contractPayFromHourly } from '../../utils/salaryCalc';

const CONTRACT_TYPES = [
  { value: 'indefinite',   label: 'Kontratë pa Afat (e Pacaktuar)' },
  { value: 'fixed_term',   label: 'Kontratë me Afat të Caktuar' },
  { value: 'probationary', label: 'Kontratë Provuese' },
  { value: 'part_time',    label: 'Kontratë me Kohë të Pjesshme' },
  { value: 'seasonal',     label: 'Kontratë Sezonale' },
  { value: 'internship',   label: 'Kontratë Praktike / Internship' },
];

const WORK_LOCATIONS = [
  { value: 'office',  label: 'Zyrë' },
  { value: 'remote',  label: 'Remote / Distancë' },
  { value: 'hybrid',  label: 'Hibride' },
  { value: 'field',   label: 'Terren' },
];

const MIN_SALARY = 264;

export default function EditContract() {
  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [errors, setErrors]         = useState({});
  const [saved, setSaved]           = useState(false);
  const [departments, setDepts]     = useState([]);
  const [branches, setBranches]     = useState([]);
  const [workTypes, setWorkTypes]   = useState([]);
  const payRates = useContractPayRates();

  const [form, setForm] = useState({
    WorkerId:        '',
    ContractType:    'indefinite',
    StartDate:       '',
    EndDate:         '',
    ProbationMonths: 3,
    Position:        '',
    Department:      '',
    BranchId:        '',
    WorkType:        '',
    WorkLocation:    'office',
    WorkHoursWeek:   40,
    BaseSalary:      '',
    HourSalary:      '',
    HourSalaryWeekend: '',
    HourSalaryHoliday: '',
    BankAccount:     '',
    BankName:        '',
    SalaryType:      'Bruto',
    TermsOfContract: '',
    Notes:           '',
  });

  // Custom fields hook — loads defs + existing values by contract id
  const {
    fields: customFields,
    sections: customSections,
    values: customValues,
    setValues: setCustomValues,
    loading: customFieldsLoading,
    saveValues: saveCustomValues,
  } = useCustomFields('contracts', id);

  // Load contract data
  useEffect(() => {
    if (!id) { setLoading(false); return; }
    let cancelled = false;

    Promise.all([
      apiService().query('getContract', 'id=' + id),
      apiService().get('department'),
      apiService().get('branches'),
      apiService().get('worktype'),
    ]).then(([cRes, dRes, bRes, wtRes]) => {
      if (cancelled) return;
      if (cRes?.success && cRes.data?.[0]) {
        const c = cRes.data[0];
        setForm(prev => ({
          ...prev,
          WorkerId:        c.WorkerId  ?? c.worker_id   ?? '',
          ContractType:    c.ContractType ?? c.contract_type ?? 'indefinite',
          StartDate:       (c.StartDate ?? c.start_date ?? '').slice(0, 10),
          EndDate:         (c.EndDate   ?? c.end_date   ?? '').slice(0, 10),
          ProbationMonths: c.ProbationMonths ?? c.probation_months ?? 3,
          Position:        c.Position   ?? c.position   ?? '',
          Department:      c.Department ?? c.department ?? '',
          BranchId:        c.BranchId ?? c.branch_id ?? '',
          WorkType:        c.WorkType ?? c.work_type ?? '',
          WorkLocation:    c.WorkLocation ?? c.work_location ?? 'office',
          WorkHoursWeek:   c.WorkHoursWeek ?? c.work_hours_week ?? 40,
          BaseSalary:      c.BaseSalary ?? c.base_salary ?? c.Salary ?? '',
          HourSalary:      c.HourSalary ?? c.hour_salary ?? '',
          HourSalaryWeekend: c.HourSalaryWeekend ?? c.hour_salary_weekend ?? '',
          HourSalaryHoliday: c.HourSalaryHoliday ?? c.hour_salary_holiday ?? '',
          BankAccount:     c.BankAccount ?? c.bank_account ?? '',
          BankName:        c.BankName   ?? c.bank_name   ?? '',
          SalaryType:      c.SalaryType ?? c.salary_type ?? 'Bruto',
          TermsOfContract: c.TermsOfContract ?? c.terms_of_contract ?? '',
          Notes:           c.Notes ?? c.notes ?? '',
        }));
      }
      if (dRes?.success) setDepts(dRes.data || []);
      if (bRes?.success) setBranches((bRes.data || []).filter((b) => b.Active !== false));
      setWorkTypes(workTypeOptions(wtRes?.success ? wtRes.data : []));
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id]);

  const set = (field) => (e) => {
    const val = e.target.value;
    // Paga bazë ↔ për orë: njëra llogarit të tjerat (LP Neni 20, shtesat e politikës fiskale).
    if (field === 'BaseSalary' || field === 'HourSalary') {
      const next = field === 'BaseSalary' ? contractPayFromBase(val, payRates) : contractPayFromHourly(val, payRates);
      setForm(prev => ({ ...prev, ...Object.fromEntries(Object.entries(next).map(([k, v]) => [k, k === field ? val : String(v)])) }));
      if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
      return;
    }
    setForm(prev => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.ContractType) e.ContractType = 'Zgjidhni llojin e kontratës';
    if (!form.StartDate)    e.StartDate    = 'Data e fillimit është e detyrueshme';
    if (!form.Position)     e.Position     = 'Pozita është e detyrueshme';
    if (!form.BaseSalary || Number(form.BaseSalary) < MIN_SALARY)
      e.BaseSalary = `Paga duhet të jetë ≥ ${MIN_SALARY}€`;
    if (['fixed_term', 'seasonal', 'probationary', 'internship'].includes(form.ContractType) && !form.EndDate)
      e.EndDate = 'Data e mbarimit është e detyrueshme';
    if (form.EndDate && form.StartDate && form.EndDate <= form.StartDate)
      e.EndDate = 'Data e mbarimit duhet të jetë pas datës së fillimit';
    if (Number(form.WorkHoursWeek) > 48)
      e.WorkHoursWeek = 'Orët e punës nuk mund të kalojnë 48h/javë (LP)';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      const payload = {
        Id:                 Number(id),
        WorkerId:           Number(form.WorkerId),
        ContractType:       form.ContractType,
        StartDate:          form.StartDate,
        EndDate:            form.EndDate || null,
        ProbationMonths:    form.ContractType === 'probationary' ? Number(form.ProbationMonths) : null,
        Position:           form.Position,
        Department:         form.Department ? Number(form.Department) : null,
        BranchId:           form.BranchId ? Number(form.BranchId) : null,
        WorkType:           form.WorkType ? Number(form.WorkType) : undefined,
        WorkLocation:       form.WorkLocation,
        WorkHoursWeek:      Number(form.WorkHoursWeek),
        Salary:             Number(form.BaseSalary),
        BaseSalary:         Number(form.BaseSalary),
        HourSalary:         form.HourSalary         ? Number(form.HourSalary)         : null,
        HourSalaryWeekend:  form.HourSalaryWeekend   ? Number(form.HourSalaryWeekend)  : null,
        HourSalaryHoliday:  form.HourSalaryHoliday   ? Number(form.HourSalaryHoliday)  : null,
        BankAccount:        form.BankAccount,
        BankName:           form.BankName,
        SalaryType:         form.SalaryType,
        TermsOfContract:    form.TermsOfContract,
        Notes:              form.Notes,
      };
      await apiService().put('workerContract', payload);

      // Save custom field values
      if (Object.keys(customValues).some(k => customValues[k] !== '' && customValues[k] != null)) {
        try {
          await configService.saveCustomFieldValues('contracts', id, customValues);
        } catch (cfErr) { console.error('Failed to save contract custom fields:', cfErr); }
      } else {
        // Still attempt to save even when all values are empty (handles deletion of values)
        await saveCustomValues();
      }

      setSaved(true);
      setTimeout(() => history.push('/contractlist'), 1200);
    } catch (err) {
      setErrors({ _general: 'Ruajtja dështoi. Provo sërisht.' });
    } finally {
      setSaving(false);
    }
  };

  const needsEndDate = ['fixed_term', 'seasonal', 'probationary', 'internship'].includes(form.ContractType);
  const showProbation = form.ContractType === 'probationary';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Ndrysho Kontratën"
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/contractlist')} variant="outlined">
            Kthehu
          </Button>
        }
      />

      {saved && <Alert severity="success" sx={{ mb: 2 }}>Kontrata u ruajt me sukses! Duke ridrejtuar…</Alert>}
      {errors._general && <Alert severity="error" sx={{ mb: 2 }}>{errors._general}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Të dhënat kryesore
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth required label="Lloji i Kontratës"
                value={form.ContractType} onChange={set('ContractType')}
                error={!!errors.ContractType} helperText={errors.ContractType}
              >
                {CONTRACT_TYPES.map(ct => (
                  <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth required label="Pozita / Titulli i Punës"
                value={form.Position} onChange={set('Position')}
                error={!!errors.Position} helperText={errors.Position}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Filiali"
                value={form.BranchId} onChange={set('BranchId')}
              >
                <MenuItem value="">— Pa filial —</MenuItem>
                {branches.map(b => (
                  <MenuItem key={b.Id} value={b.Id}>{b.Name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Lloji i punës"
                value={form.WorkType} onChange={set('WorkType')}
              >
                {workTypes.map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Departamenti"
                value={form.Department} onChange={set('Department')}
              >
                <MenuItem value="">— Pa departament —</MenuItem>
                {departmentsForBranch(departments, form.BranchId).map(d => (
                  <MenuItem key={d.Id} value={d.Id}>{d.Name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Vendndodhja e Punës"
                value={form.WorkLocation} onChange={set('WorkLocation')}
              >
                {WORK_LOCATIONS.map(l => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth type="number" label="Orët e Punës / Javë"
                value={form.WorkHoursWeek} onChange={set('WorkHoursWeek')}
                inputProps={{ min: 1, max: 48 }}
                error={!!errors.WorkHoursWeek} helperText={errors.WorkHoursWeek || 'Standardi: 40h/javë'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Datat
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="date" label="Data e Fillimit"
                InputLabelProps={{ shrink: true }}
                value={form.StartDate} onChange={set('StartDate')}
                error={!!errors.StartDate} helperText={errors.StartDate}
              />
            </Grid>
            {needsEndDate && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth required type="date" label="Data e Mbarimit"
                  InputLabelProps={{ shrink: true }}
                  value={form.EndDate} onChange={set('EndDate')}
                  error={!!errors.EndDate} helperText={errors.EndDate}
                />
              </Grid>
            )}
            {showProbation && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select fullWidth label="Muajt e Provës (maks 6)"
                  value={form.ProbationMonths} onChange={set('ProbationMonths')}
                >
                  {[1, 2, 3, 4, 5, 6].map(m => (
                    <MenuItem key={m} value={m}>{m} muaj</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Paga & Shpërblimet
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="number"
                label={`Paga Bazë Bruto (min ${MIN_SALARY}€)`}
                value={form.BaseSalary} onChange={set('BaseSalary')}
                InputProps={{ endAdornment: '€' }}
                error={!!errors.BaseSalary} helperText={errors.BaseSalary}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select fullWidth label="Lloji i Pagës"
                value={form.SalaryType} onChange={set('SalaryType')}
              >
                <MenuItem value="Bruto">Bruto</MenuItem>
                <MenuItem value="Neto">Neto</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Paga për orë (€)"
                helperText={`= paga ÷ 173,33 · fundjavë +${payRates.WeekendRate}%, festë +${payRates.HolidayRate}%`}
                value={form.HourSalary} onChange={set('HourSalary')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Fundjavë / Orë (€)"
                value={form.HourSalaryWeekend} onChange={set('HourSalaryWeekend')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Festë Zyrtare / Orë (€)"
                value={form.HourSalaryHoliday} onChange={set('HourSalaryHoliday')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Të dhënat bankare
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth label="IBAN / Numri i llogarisë"
                value={form.BankAccount} onChange={set('BankAccount')}
                helperText="Format: XK05 1212 …"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth label="Banka"
                value={form.BankName} onChange={set('BankName')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Kushtet
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={4} label="Kushtet e Kontratës (Neni shtesë)"
                value={form.TermsOfContract} onChange={set('TermsOfContract')}
                placeholder="Kushte specifike, klauzola konfidencialiteti, etj."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Shënime"
                value={form.Notes} onChange={set('Notes')}
              />
            </Grid>
          </Grid>

          {/* Dynamic custom fields from Module Management */}
          <ModuleCustomFields
            moduleKey="contracts"
            fields={customFields}
            sections={customSections}
            values={customValues}
            onChange={setCustomValues}
            formType="edit"
            loading={customFieldsLoading}
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || saved}
        >
          {saving ? 'Duke ruajtur…' : 'Ruaj Ndryshimet'}
        </Button>
        <Button variant="outlined" onClick={() => history.push('/contractlist')}>
          Anulo
        </Button>
      </Stack>
    </Box>
  );
}
